import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import TextField from '@mui/material/TextField';
import { countNumeros, parseNumeros } from '../utils/parseNumeros';

const COUNT_DEBOUNCE_MS = 200;

const Textarea = forwardRef(function Textarea({ onNumerosCountChange }, ref) {
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const rafRef = useRef(null);
  const [numerosCount, setNumerosCount] = useState(0);
  const [counting, setCounting] = useState(false);

  const updateCount = useCallback(
    (value) => {
      setCounting(true);
      const total = countNumeros(value);
      setNumerosCount(total);
      setCounting(false);
      onNumerosCountChange?.(total);
    },
    [onNumerosCountChange],
  );

  const scheduleCount = useCallback(
    (value) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      setCounting(true);

      debounceRef.current = setTimeout(() => {
        rafRef.current = requestAnimationFrame(() => {
          updateCount(value);
        });
      }, COUNT_DEBOUNCE_MS);
    },
    [updateCount],
  );

  useImperativeHandle(ref, () => ({
    getText: () => inputRef.current?.value ?? '',
    getNumeros: () => parseNumeros(inputRef.current?.value ?? ''),
    setText: (value) => {
      if (inputRef.current) {
        inputRef.current.value = value;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        updateCount(value);
      });
    },
    clear: () => {
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      setNumerosCount(0);
      setCounting(false);
      onNumerosCountChange?.(0);
    },
  }));

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    },
    [],
  );

  return (
    <div className="app-textarea">
      <p className="app-panel-label">Ingreso manual</p>
      <TextField
        inputRef={inputRef}
        defaultValue=""
        multiline
        minRows={8}
        maxRows={12}
        fullWidth
        color="primary"
        placeholder="Ingresar Numeros telefonicos..."
        onChange={(e) => scheduleCount(e.target.value)}
        helperText={
          <span className="app-helper-text font-space-regular">
            {counting && numerosCount === 0
              ? '...'
              : `Numeros detectados: ${numerosCount.toLocaleString('es-AR')}`}
          </span>
        }
        className="app-textarea__field"
      />
    </div>
  );
});

export default Textarea;
